import type { Phase } from '@banger/shared';

const KEY = 'banger.player';

/** Auto-rejoin only if the phone saw the room this recently. */
export const REJOIN_FRESH_MS = 3 * 60 * 60 * 1000;
/** A finished match (finale) is only worth rejoining for this long. */
export const FINALE_FRESH_MS = 10 * 60 * 1000;

export interface RememberedPlayer {
  /** '' once the room is forgotten (name and emoji are kept for the next join). */
  code: string;
  name: string;
  emoji: string;
  /** ms when the last view from this room arrived. */
  lastSeen?: number;
  /** Phase of that last view. */
  phase?: Phase;
}

export function loadPlayer(): RememberedPlayer | null {
  try {
    const p = JSON.parse(localStorage.getItem(KEY) ?? 'null') as RememberedPlayer | null;
    return p && typeof p.code === 'string' && typeof p.name === 'string' && typeof p.emoji === 'string' ? p : null;
  } catch { return null; }
}

function write(p: RememberedPlayer): void {
  try { localStorage.setItem(KEY, JSON.stringify(p)); } catch { /* private mode */ }
}

/** Remember a fresh join. */
export function savePlayer(p: { code: string; name: string; emoji: string }, now = Date.now()): void {
  write({ code: p.code, name: p.name, emoji: p.emoji, lastSeen: now });
}

/** A view arrived from `code`: note when, and in which phase. */
export function touchPlayer(code: string, phase: Phase, now = Date.now()): void {
  const p = loadPlayer();
  if (p && p.code === code) write({ ...p, lastSeen: now, phase });
}

/** Forget the room but keep name and emoji for the next join. */
export function forgetRoom(): void {
  const p = loadPlayer();
  if (p) write({ code: '', name: p.name, emoji: p.emoji });
}

/** Worth auto-rejoining: seen within 3 h, and within 10 min if the match had ended. */
export function isFresh(p: RememberedPlayer, now: number): boolean {
  if (!p.code || typeof p.lastSeen !== 'number') return false;
  const age = now - p.lastSeen;
  if (age > REJOIN_FRESH_MS) return false;
  if (p.phase === 'finale' && age > FINALE_FRESH_MS) return false;
  return true;
}

/** The remembered player, with a stale room code dropped (and forgotten in storage). */
export function loadPlayerForRejoin(now = Date.now()): RememberedPlayer | null {
  const p = loadPlayer();
  if (!p || !p.code || isFresh(p, now)) return p;
  forgetRoom();
  return { code: '', name: p.name, emoji: p.emoji };
}
